import { Ref } from 'vue'
import { useOrderStore } from '~/stores/order'
import { ORDER_LIMIT } from '~/utils/constants'
import { Layer, Schema, SchemaObject, Tools, View } from '../utils/scheme-designer'

interface orderSchema {
  event: string
  cart: any[]
  user: {
    email: string
    firstName: string
    secondName: string
    phone: string
  }
}

let orderStore: orderSchema = {
  event: '',
  cart: [],
  user: {
    email: '',
    firstName: '',
    secondName: '',
    phone: ''
  }
}

export interface SchemaData {
  ID: string
  ObjectType: string
  Width: string
  Height: string
  CX: string
  CY: string
  Angle: string
  Row: string
  Seat: string
  Name_sec: string
  FontColor: string
  FontSize: string
  Label: string
  BackColor: string
  Price: string
  avail: boolean
}

export interface ISchemaRendererParams {
  clickFunction?: (schemaObject: SchemaObject, schemaDesigner: Schema, view: View, e: Event) => void
  onClickOnObject?: (e: Event) => void
  placeRenderer?: (schemaObject: SchemaObject, schemaDesigner: Schema, view: View) => void
  labelRenderer?: (schemaObject: SchemaObject, schemaDesigner: Schema, view: View) => void
}

export const SCHEMA_RENDERER_DEFAULT_PARAMS: Required<ISchemaRendererParams> = {
  clickFunction(schemaObject: SchemaObject, _schemaDesigner: Schema, _view: View, _e: Event) {
    const objectParams = schemaObject.getParams()
    if (objectParams.isSelected || orderStore.cart.length < ORDER_LIMIT) {
      if (Number(objectParams.price) > 0) {
        objectParams.isSelected = !objectParams.isSelected
      }
    }
  },
  onClickOnObject(_e: Event) {},
  placeRenderer(schemaObject: SchemaObject, _schemaDesigner: Schema, view: View) {
    let isRoundRect = true

    orderStore = useOrderStore()
    const context = view.getContext()

    try {
      if (context.roundRect) isRoundRect = true
    } catch (error) {
      isRoundRect = false
      console.warn('roundRect is not supporting, using rect')
    }

    const objectParams = schemaObject.getParams()

    if (objectParams.type !== 'Place') return context.restore()

    const { avail: isAvailable } = objectParams

    const backgroundColor = '#' + objectParams.backgroundColor

    context.beginPath()
    context.lineWidth = 4

    let placeState: 'avail' | 'not-avail' | 'hovered' | 'selected' = 'not-avail'

    if (isAvailable && Number(objectParams.price) > 0) {
      placeState = 'avail'

      const isHovered =
        schemaObject.isHovered && !Tools.touchSupported() && orderStore.cart.length < ORDER_LIMIT

      if (objectParams.isSelected) {
        placeState = 'selected'
      } else if (isHovered) {
        placeState = 'hovered'
      } else if (objectParams.isSelected) {
        placeState = 'selected'
      }
    }

    switch (placeState) {
      case 'not-avail': {
        context.strokeStyle = '#f7f7f7'
        context.fillStyle = '#d9d9d9' 

        let relativeX = schemaObject.x
        let relativeY = schemaObject.y

        let width = schemaObject.getWidth()
        let height = schemaObject.getHeight()

        const borderOffset = 4
        relativeX = relativeX + borderOffset
        relativeY = relativeY + borderOffset
        width = width - borderOffset * 2
        height = height - borderOffset * 2

        isRoundRect
          ? context.roundRect(relativeX, relativeY, width, height, [height / 2])
          : context.rect(relativeX, relativeY, width, height)

        context.fill()
        context.stroke()

        return context.restore()
      }
      case 'avail': {
        context.strokeStyle = '#f7f7f7'
        context.fillStyle = backgroundColor
        let relativeX = schemaObject.x
        let relativeY = schemaObject.y
        let width = schemaObject.getWidth()
        let height = schemaObject.getHeight()

        const borderOffset = 4
        relativeX = relativeX + borderOffset
        relativeY = relativeY + borderOffset
        width = width - borderOffset * 2
        height = height - borderOffset * 2

        isRoundRect
          ? context.roundRect(relativeX, relativeY, width, height, [height / 2])
          : context.rect(relativeX, relativeY, width, height)

        context.fill()
        context.stroke()

        return context.restore()
      }
      case 'hovered': {
        context.fillStyle = '#f7f7f7'
        context.strokeStyle = backgroundColor
        let relativeX = schemaObject.x
        let relativeY = schemaObject.y
        let width = schemaObject.getWidth()
        let height = schemaObject.getHeight()

        const borderOffset = 4
        relativeX = relativeX + borderOffset
        relativeY = relativeY + borderOffset
        width = width - borderOffset * 2
        height = height - borderOffset * 2

        isRoundRect
          ? context.roundRect(relativeX, relativeY, width, height, [height / 2])
          : context.rect(relativeX, relativeY, width, height)

        context.fill()
        context.stroke()

        context.font = `${Math.floor((width + height) / 4)}px Sf Pro`
        context.textAlign = 'center'
        context.textBaseline = 'bottom'
        context.fillStyle = '#000000'

       let half = (width + height) / 4
        context.fillText(
          objectParams.labelName,
          schemaObject.getX() + width / 2 + half / 16,
          schemaObject.getY() + height - height / 5
        )

        return context.restore()
      }
      case 'selected': {
        context.strokeStyle = '#f7f7f7'
        context.fillStyle = backgroundColor
        let relativeX = schemaObject.x
        let relativeY = schemaObject.y
        let width = schemaObject.getWidth()
        let height = schemaObject.getHeight()

        const borderOffset = 4
        relativeX = relativeX + borderOffset
        relativeY = relativeY + borderOffset
        width = width - borderOffset * 2
        height = height - borderOffset * 2

        isRoundRect
          ? context.roundRect(relativeX, relativeY, width, height, [height / 2])
          : context.rect(relativeX, relativeY, width, height)

        context.fill()
        context.stroke()

        context.font = `${Math.floor((width + height) / 4)}px Sf Pro`
        context.textAlign = 'center'
        context.textBaseline = 'bottom'
        context.fillStyle = '#f7f7f7'

       let half = (width + height) / 4
       context.fillText(
        objectParams.labelName,
        schemaObject.getX() + width / 2 + half / 16,
        schemaObject.getY() + height - height / 5
      )

        return context.restore()
      }
      default:
        return context.restore()
    }
  },
  labelRenderer(schemaObject: SchemaObject, _schemaDesigner: Schema, view: View) {
    const context = view.getContext()

    const objectParams = schemaObject.getParams()
    // const fontSize = (objectParams.fontSize >> 0) * (96 / 72) * 3

    if (objectParams.type !== 'Label') return context.restore()

    let color = objectParams.backgroundColor
    
    if(objectParams.backgroundColor==='opacity') {
      color = 'f7f7f7'
    }

    const backgroundColor = '#' + color
    const fontColor = '#' + objectParams.fontColor

    let relativeX = schemaObject.x
    let relativeY = schemaObject.y

    let width = schemaObject.getWidth()
    let height = schemaObject.getHeight()

    const halfWidth = width / 2
    const halfHeight = height / 2

    context.beginPath()
    context.lineWidth = 4

    context.save()

    context.strokeStyle = '#f7f7f7'
    context.fillStyle = backgroundColor

    let radius = [height / 2]

    if (objectParams.sectorName === 'Circular_scene') {
      radius = [0, 0, 1000, 1000]
    }

    if (schemaObject.getRotation()) {
      // TODO: crashing. need fix
      // context.save()
      // context.translate(relativeX + halfWidth, relativeY + halfHeight)
      // context.rotate((schemaObject.getRotation() * Math.PI) / 180)
      // context.rect(-halfWidth, -halfHeight, width, height)
      if (context.roundRect) {
        context.roundRect(relativeX, relativeY, width, height, radius)
      } else {
        context.rect(relativeX, relativeY, width, height)
      }
      context.fill()
    } else if (context.roundRect) {
      if (objectParams.sectorName === 'Scene') {
        context.beginPath()
        context.strokeStyle = '#e2e2e2'
        context.lineWidth = 7
        context.moveTo(relativeX - 45, relativeY)
        context.bezierCurveTo(
          80 + relativeX,
          relativeY - 64,
          width - 77 + relativeX,
          relativeY - 64,
          width + 40 + relativeX,
          relativeY
        )
        context.stroke()
      } 
      else if (objectParams.sectorName === 'Top_scene') {
        context.beginPath()
        context.strokeStyle = '#e2e2e2'
        context.lineWidth = 7
        context.moveTo(relativeX - 120, relativeY)
        context.bezierCurveTo(
          130 + relativeX,
          relativeY + 194,
          width - 127 + relativeX,
          relativeY + 185,
          width + 130 + relativeX,
          relativeY
        )
        context.stroke()
      }
      else if (objectParams.sectorName === 'Circular_scene') {
        context.beginPath()
        context.strokeStyle = '#e2e2e2'
        context.lineWidth = 7
        context.moveTo(relativeX - 45, relativeY)
        context.bezierCurveTo(
          80 + relativeX,
          1400 + relativeY,
          width - 50 + relativeX,
          1400 + relativeY,
          width + 40 + relativeX,
          relativeY
        )
        context.stroke()
      } else {
        if(objectParams.backgroundColor==='opacity') {
          context.strokeStyle = 'black'
        }
       context.roundRect(relativeX, relativeY, width, height, radius)
       context.fill()
      }
    } else {
      context.rect(relativeX, relativeY, width, height)
      context.fill()
    }
    // context.fill()
    context.stroke()
    context.font = objectParams.fontSize + 'px Sf Pro'
    context.textAlign = 'center'
    context.textBaseline = 'middle'
    context.fillStyle = fontColor

    if (schemaObject.getRotation()) {
      context.save()
      context.translate(relativeX + halfWidth, relativeY + halfHeight)
      context.rotate((schemaObject.getRotation() * Math.PI) / 180)
      context.fillText(
        objectParams.labelName,
        -(schemaObject.getWidth() / 2) + schemaObject.getWidth() / 2,
        -(schemaObject.getHeight() / 2) + schemaObject.getHeight() / 2
      )
    } else {
      context.fillText(
        objectParams.labelName,
        schemaObject.getX() + halfWidth,
        schemaObject.getY() + halfHeight
      )
    }

    return context.restore()
  }
}

export function useSchemaRenderer(schema: Ref<SchemaData[]>, params: ISchemaRendererParams = {}) {
  let schemaDesigner: Schema

  // @ts-ignore
  const canvas = ref<Ref<HTMLCanvasElement>>(undefined)

  // @ts-ignore
  // const mapCanvas = ref<Ref<HTMLCanvasElement>>(undefined);

  const scale = ref(1)

  function zoomIn() {
    schemaDesigner.getZoomManager().zoomToCenter(10)
  }

  function zoomOut() {
    schemaDesigner.getZoomManager().zoomToCenter(-10)
  }

  const backgroundLayer = new Layer('background', {
    zIndex: 0,
    visible: false,
    active: false
  })

  backgroundLayer.addObject(
    new SchemaObject({
      x: 0.5,
      y: 0.5,
      width: 8600,
      height: 7000,
      renderFunction: function (schemaObject: SchemaObject, _schemaDesigner: Schema, view: View) {
        const context = view.getContext()
        context.beginPath()
        context.lineWidth = 4
        context.strokeStyle = 'rgba(12, 200, 15, 0.2)'

        context.fillStyle = 'rgba(12, 200, 15, 0.2)'

        const width = schemaObject.width
        const height = schemaObject.height

        context.rect(schemaObject.x, schemaObject.y, width, height)

        context.fill()
        context.stroke()
      },
      clickFunction(schemaObject: SchemaObject, _schemaDesigner: Schema, _view: View, _e: Event) {
        const objectParams = schemaObject.getParams()
        objectParams.isSelected = !objectParams.isSelected
      }
    })
  )

  const defaultLayer = new Layer('defaultLayer', { zIndex: 10 })

  function render() {
    try {
      schemaDesigner = new Schema(canvas.value, {
        options: {
          background: '#F7F7F7',
          cacheSchemaRatio: 2
        },
        scroll: {
          maxHiddenPart: 0.5
        },
        zoom: {
          padding: 0.5,
          maxScale: 4,
          minScale: 0.5,
          zoomCoefficient: 1.04,
          clickZoomDelta: 14
        },
        storage: {
          treeDepth: 6
        }
        // map: {
        //   mapCanvas: mapCanvas.value,
        // },
      })

      schemaDesigner.addLayer(backgroundLayer)

      for (const objectData of schema.value) {
        const leftOffset = Number(objectData.CX) >> 0
        const topOffset = Number(objectData.CY) >> 0
        const width = Number(objectData.Width) >> 0
        const height = Number(objectData.Height) >> 0
        const rotation = Number(objectData.Angle) >> 0
        
        const schemaObject = new SchemaObject({
          x: 0.5 + leftOffset,
          y: 0.5 + topOffset,
          width,
          height,
          type: objectData.ObjectType,
          active: objectData.ObjectType === 'Place' && Number(objectData.Price) > 0,
          cursorStyle: objectData.ObjectType === 'Place' ? 'pointer' : 'none',
          rotation,
          id: objectData.ID,
          price: objectData.Price,
          seat: objectData.Seat,
          row: objectData.Row,
          sectorName: objectData.Name_sec,
          labelName: objectData.Label,
          fontSize: objectData.FontSize,
          backgroundColor: objectData.BackColor,
          fontColor: objectData.FontColor,
          isSelected: false,
          avail: objectData.avail,
          renderFunction:
            objectData.ObjectType === 'Place'
              ? params?.placeRenderer || SCHEMA_RENDERER_DEFAULT_PARAMS.placeRenderer
              : params?.labelRenderer || SCHEMA_RENDERER_DEFAULT_PARAMS.labelRenderer
        })

        schemaObject.setClickFunction(
          params?.clickFunction || SCHEMA_RENDERER_DEFAULT_PARAMS.clickFunction
        )
        schemaObject.setCursorStyle(objectData.ObjectType === 'Place' ? 'pointer' : 'default')

        defaultLayer.addObject(schemaObject)
      }

      schemaDesigner.addLayer(defaultLayer)
    } catch (e) {
      console.error(e)
    }

    schemaDesigner.render()

    // canvas.value.addEventListener('schemaDesigner.beforeRenderAll', function (e) {
    //   // console.time('renderAll');
    // }, false);

    // canvas.value.addEventListener('schemaDesigner.afterRenderAll', function (e) {
    //   // console.timeEnd('renderAll');
    // }, false);

    canvas.value.addEventListener(
      'schemaDesigner.clickOnObject',
      params?.onClickOnObject || SCHEMA_RENDERER_DEFAULT_PARAMS.onClickOnObject,
      false
    )

    // canvas.value.addEventListener('schemaDesigner.mouseOverObject', function (e) {
    //   // console.log('mouseOverObject', e.detail);
    // }, false);

    // canvas.value.addEventListener('schemaDesigner.mouseLeaveObject', function (e) {
    //   // console.log('mouseLeaveObject', e.detail);
    // }, false);

    canvas.value.addEventListener(
      'schemaDesigner.scroll',
      function (_e) {
        // console.log('scroll', e.detail);
      },
      false
    )
  }

  function rerender() {
    schemaDesigner.removeLayer('default')
    schemaDesigner.removeLayer('background')
    defaultLayer.removeObjects()
    schemaDesigner.clearContext()
    const context = canvas.value?.getContext('2d')
    context?.clearRect(0, 0, canvas.value?.width, canvas.value?.height)

    render()
  }

  watch(schema, () => {
    defaultLayer.removeObjects()
    schemaDesigner.removeLayer('default')
    schemaDesigner.removeLayer('background')
    schemaDesigner.clearContext()
    const context = canvas.value?.getContext('2d')
    context?.clearRect(0, 0, canvas.value?.width, canvas.value?.height)

    render()
  })

  return {
    canvas,
    // mapCanvas,
    zoomIn,
    zoomOut,
    render,
    rerender,
    scale
  }
}
